import { getDefaultTextRect } from '@src/slide/domain/ui-model/element-modeles/property/text/utils/text-rect';
import ResizeMeasureCanvas from '@src/slide/domain/ui-model/element-modeles/property/text/editor-measure/resize-measure';
import { LEVEL } from '@src/slide/domain/ui-model/element-modeles/property/text/utils/text-level-map';
import { AutoFitType } from '@src/slide/domain/ui-model/element-modeles/property/text/constants/text-style-type';
import { EditorDataModel } from '@src/slide/domain/ui-model/element-modeles/property/text/editor-data-model';
import _set from 'lodash/set';
import _get from 'lodash/get';
import _merge from 'lodash/merge';
import _pick from 'lodash/pick';
import { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';

/**
 * 影响自缩排 fontScale 的值
 * 1. 文字属性，字体字号内容等等，都包含在 model.paragraphList 中
 * 2. 形状尺寸，包含在 model.props.style 中
 * 3. 形状边距、自缩排属性等最外层的文本属性，包含在 model.textBodyStyle 中
 */
export type FontScaleDependentValues = {
    textBodyStyle?: IShapeModel['textBodyStyle'],
    paragraphList?: IShapeModel['paragraphList'],
    style?: IShapeModel['props']['style']
};

/**
 * 自缩排元素需要设置 fontScale 属性，导出到本地用于更新元素字号
 * 目前设置形状格式、缩放、格式刷、文字变更时需要设置 fontScale
 * @param element
 * @param newDependentValues
 */
export function updateFontScale(
    element: IShapeModel,
    newDependentValues: FontScaleDependentValues = {},
) {
    const fontScale = calculateFontScale(element, newDependentValues);
    const { textBodyStyle = element.textBodyStyle } = newDependentValues;
    if (typeof fontScale === 'number') {
        _set(textBodyStyle, 'wrapperStyle.fontScale', fontScale);
    }
}

/**
 * 根据当前元素属性和部分修改之后的依赖值重新计算 fontScale
 * 默认会修改 targetTextBodyStyle 并返回
 * @param element
 * @param newDependentValues
 * @param targetTextBodyStyle
 * @returns
 */
export function updateFontScaleSafety(
    element: IShapeModel,
    newDependentValues: FontScaleDependentValues = {},
    targetTextBodyStyle = {},
) {
    const fontScale = calculateFontScale(element, newDependentValues);
    if (typeof fontScale === 'number') {
        _set(targetTextBodyStyle, 'wrapperStyle.fontScale', fontScale);
    }
    return targetTextBodyStyle;
}

/**
 * 计算 fontScale，主体逻辑从原有的 updateFontScale 逻辑中抽离，无副作用
 * @param element
 * @param newDependentValues
 * @returns
 */
export function calculateFontScale(
    element: IShapeModel,
    newDependentValues: FontScaleDependentValues = {},
): number | null {
    const {
        paragraphList = element.paragraphList,
    } = newDependentValues;
    // 获取最新的全量 textBodyStyle
    const textBodyStyle = _merge({}, element.textBodyStyle, newDependentValues.textBodyStyle);
    if (textBodyStyle?.autoFitType !== AutoFitType.FONT_AUTO_FIT) {
        return null;
    }
    // 后面只用到了 style 中的 ['width', 'height', 'top', 'left']，就只合并和提取必要的数据
    const style = _merge(_pick(element.props.style, ['width', 'height', 'top', 'left']), newDependentValues.style);
    const rect = getDefaultTextRect(element, style);
    const editorValue = EditorDataModel.convertFromTextModel({
        paragraphList,
        textBodyStyle,
    });
    const options = {
        modelKey: element.key,
        textBodyStyle,
        textRect: { ...style, ...rect },
        isTitle: isTitleElement(element),
    };
    const resizeLevel = ResizeMeasureCanvas.measureResizeLevel(editorValue, options);
    if (typeof resizeLevel === 'number') {
        return LEVEL[resizeLevel];
    }
    return null;
}

export function getLevelByFontScale(fontScale: number): number {
    let level;
    Object.keys(LEVEL).forEach((key) => {
        if (LEVEL[key] === fontScale) {
            level = parseInt(key, 10);
        }
    });

    return level;
}


function isTitleElement(element)  {
    const type = element?.placeHolder?.type;
    return type === 'title' || type === 'ctrTitle';
}

export function getFontScale(textBodyStyle?: IShapeModel['textBodyStyle']) {
    return _get(textBodyStyle, 'wrapperStyle.fontScale');
}
